import axios from 'axios';
import constants from '../../config/constants';
import { updateTransportAreaService } from './transportFee';

// Plain (non-thunk) axios calls - the Transport page (views/admin/Transport) keeps its own local
// state, same as Inventory, so none of this needs a redux slice. Routes (fee structures), their
// stops and student assignment already live in actions/admin/transportFee.js, which the Routes
// tab reuses as-is.
const BASE = constants.API_BASE_URL;

const APIS = {
  bus: '/api/bus', // POST, PUT /{id}, GET /school/{schoolId}, DELETE /{id}
  driver: '/api/admin', // create-driver, get-all-drivers/{schoolId}, update-driver/{id}, delete-driver/{id}
  busDriver: '/api/bus-driver', // POST /select-route, GET /current-route
  studentTransport: '/api/student-transport', // GET /school/{schoolId}/batch/{batchId}
};

// -------------------- Buses -------------------- //

export const fetchBusesApi = data =>
  axios.get(`${BASE}${APIS.bus}/school/${data.schoolId}`, constants.setHeader(data.token));

export const saveBusApi = data => axios.post(`${BASE}${APIS.bus}`, data.postData, constants.setHeader(data.token));

export const updateBusApi = data =>
  axios.put(`${BASE}${APIS.bus}/${data.busId}`, data.postData, constants.setHeader(data.token));

export const deleteBusApi = data => axios.delete(`${BASE}${APIS.bus}/${data.busId}`, constants.setHeader(data.token));

// -------------------- Drivers -------------------- //
// The backend's own driver endpoints: create-driver makes the employee (profession DRIVING) and a
// ROLE_DRIVER login in one go; get-all-drivers lists by that profession.

export const DRIVER_DESIGNATION = 'Driver';
export const DRIVER_DEPARTMENT = 'Transport';

export const fetchDriversApi = async data => {
  const res = await axios.get(
    `${BASE}${APIS.driver}/get-all-drivers/${data.schoolId}`,
    constants.setHeader(data.token)
  );
  return res.data || [];
};

export const saveDriverApi = data =>
  axios.post(`${BASE}${APIS.driver}/create-driver`, data.postData, constants.setHeader(data.token));

export const updateDriverApi = data =>
  axios.put(`${BASE}${APIS.driver}/update-driver/${data.employeeId}`, data.postData, constants.setHeader(data.token));

export const deleteDriverApi = data =>
  axios.delete(`${BASE}${APIS.driver}/delete-driver/${data.employeeId}`, constants.setHeader(data.token));

// -------------------- Driver <-> bus <-> route -------------------- //
// The backend keeps one assignment per driver per batch and has no list-all endpoint: select-route upserts it
// (so assigning again replaces it), current-route reads one driver's, and unassign-route deletes it by its id.

export const selectRouteApi = data =>
  axios.post(`${BASE}${APIS.busDriver}/select-route`, data.postData, constants.setHeader(data.token));

// Removes one driver's bus and route assignment. The driver, the bus and the route themselves stay.
export const unassignRouteApi = data =>
  axios.delete(
    `${BASE}${APIS.busDriver}/unassign-route/${data.driverRouteAssignmentId}`,
    constants.setHeader(data.token)
  );

// Resolves to null (not an error) when the driver has no route yet - that case comes back from the
// backend as a 500 "Driver has not selected any route".
export const fetchCurrentRouteApi = async data => {
  try {
    const res = await axios.get(
      `${BASE}${APIS.busDriver}/current-route${constants.makeParams({
        employeeId: data.employeeId,
        schoolId: data.schoolId,
        batchId: data.batchId,
      })}`,
      constants.setHeader(data.token)
    );
    return res.data || null;
  } catch (err) {
    return null;
  }
};

// Every driver's assignment for a batch. The backend has no list-all, so this asks once per driver.
export const fetchDriverAssignments = async ({ drivers, schoolId, batchId, token }) => {
  if (!batchId) return [];
  const rows = await Promise.all(
    drivers.map(driver => fetchCurrentRouteApi({ employeeId: driver.employeeId, schoolId, batchId, token }))
  );
  return rows.filter(Boolean);
};

// A live read (not a page's already-loaded copy), for checks right before a delete: assignments are made
// from several places and by other admins, so a cached list can be out of date.
export const fetchSchoolAssignments = async ({ schoolId, batchId, token }) => {
  const drivers = await fetchDriversApi({ schoolId, token });
  return fetchDriverAssignments({ drivers, schoolId, batchId, token });
};

// -------------------- Route <-> stop name -------------------- //

// The backend finds a driver's students by matching the stops' `ruteName` against the assignment's
// route name as exact text (it never checks either against the route itself). A stop saved under any
// other name - older data, or another screen - is therefore silently missing for the driver. Every stop
// written from this UI already carries its route's name; this rewrites the ones that don't. Resolves to
// how many stops were changed.
export const syncRouteStopNames = async ({ route, areas, token }) => {
  const stale = (areas || []).filter(area => area.ruteName !== route.description);
  await Promise.all(
    stale.map(area => updateTransportAreaService({ token, postData: { ...area, ruteName: route.description } })())
  );
  return stale.length;
};

// -------------------- Students on routes -------------------- //

export const fetchSchoolBatchStudentTransportApi = data =>
  axios.get(
    `${BASE}${APIS.studentTransport}/school/${data.schoolId}/batch/${data.batchId}`,
    constants.setHeader(data.token)
  );

// -------------------- Bus attendance reports -------------------- //
// Pickup/drop attendance marked by drivers in the mobile app. Daily returns a plain list; weekly (the Mon-Sun
// week of `date`) and monthly return totals plus the records.
const ATTENDANCE_API = '/api/bus-attendance';

export const fetchBusAttendanceDailyApi = data =>
  axios.get(
    `${BASE}${ATTENDANCE_API}/daily-report${constants.makeParams({
      schoolId: data.schoolId,
      batchId: data.batchId,
      date: data.date,
    })}`,
    constants.setHeader(data.token)
  );

export const fetchBusAttendanceWeeklyApi = data =>
  axios.get(
    `${BASE}${ATTENDANCE_API}/weekly-report${constants.makeParams({
      schoolId: data.schoolId,
      batchId: data.batchId,
      date: data.date,
    })}`,
    constants.setHeader(data.token)
  );

export const fetchBusAttendanceMonthlyApi = data =>
  axios.get(
    `${BASE}${ATTENDANCE_API}/monthly-report${constants.makeParams({
      schoolId: data.schoolId,
      batchId: data.batchId,
      year: data.year,
      month: data.month,
    })}`,
    constants.setHeader(data.token)
  );

// -------------------- Admin: mark bus attendance -------------------- //
// The attendance endpoints take the driver's employeeId, so an admin marks for a bus through the driver assigned to it.

// The students of the route a driver is assigned to, each with the status already marked for that day and trip.
export const fetchRouteStudentsApi = data =>
  axios.get(
    `${BASE}${ATTENDANCE_API}/route-students${constants.makeParams({
      employeeId: data.employeeId,
      schoolId: data.schoolId,
      batchId: data.batchId,
      tripType: data.tripType,
      date: data.date,
    })}`,
    constants.setHeader(data.token)
  );

// students: [{ studentId, status: 'PICKED' | 'DROPPED' | 'ABSENT' }]
export const markBusAttendanceApi = data =>
  axios.post(`${BASE}${ATTENDANCE_API}/mark`, data.postData, constants.setHeader(data.token));

// -> { schoolId, batchId, totalTransportStudents, routes: [{ transportFeeStructId, routeName, studentCount }] }
export const fetchTransportStudentCountApi = async ({ schoolId, batchId, token }) => {
  const res = await axios.get(
    `${BASE}${APIS.studentTransport}/student/count${constants.makeParams({ schoolId, batchId })}`,
    constants.setHeader(token)
  );
  return res.data;
};

// GET /api/reports/transport/route/{routeId}?batchId=&pendingOnly= (routeId = the route's transportFeeStructId) -> one row per
// student on the route: monthly fee, paid, balance, what is due so far, paid / pending months and the guardian's contact
// (paymentStatus PAID | DUE | UP_TO_DATE).
export const fetchTransportFeeReportApi = ({ routeId, batchId, pendingOnly = false, token }) =>
  axios.get(
    `${constants.API_BASE_URL}/api/reports/transport/route/${routeId}${constants.makeParams({ batchId, pendingOnly })}`,
    constants.setHeader(token)
  );
